import { Stack, useTheme } from '@mui/material';
import { useReactFlow } from '@xyflow/react';

import { FormConfigurationProvider } from '@/components/form/context/FormConfigurationContext';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { GraphVizNodeConfigFormField } from '@/modules/graphViz/graphql/GraphVizNodeConfigFormFields';
import {
  GraphVizIconShape,
  GraphVizNodeComponentType,
} from '@/modules/graphViz/graphVizNodeConfig';
import { TileNotesFieldWithGenerate } from '@/modules/summaryPanels/TileNotesSummaryPanel/TileNotesFieldWithGenerate';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { EntityGraphNodeConfiguration } from '@/types/schema';

import { useNode } from '../../hooks/useNode';
import { TileNode } from '../../types';

export interface TileEditModalFormShape {
  notes?: string;
  componentType: GraphVizNodeComponentType;
  iconShape?: GraphVizIconShape;
  estateWaterfallConfig: EntityGraphNodeConfiguration['estateWaterfallConfig'];
}

interface TileEditModalInnerProps {
  isOpen: boolean;
  onSuccess?: () => void;
  onClose: () => void;
  node: TileNode;
  showEstateWaterfallTaxBreakoutInEditModal: boolean;
  /**
   * If provided, this callback is called with the form data instead of
   * updating React Flow directly. Used for deferred persistence mode where
   * updates should go through the reducer for proper undo/redo support.
   */
  onSaveData?: (formData: TileEditModalFormShape) => void;
}

function TileEditModalInner({
  isOpen,
  onSuccess: onSuccessExternal,
  onClose,
  node,
  showEstateWaterfallTaxBreakoutInEditModal,
  onSaveData,
}: TileEditModalInnerProps) {
  const theme = useTheme();
  const { updateNodeData, updateNode, getNode } = useReactFlow();
  const { formRef, handleSave } = useFormSaveHandler();
  const { formState, handleSubmit, reset, control } =
    useFormContext<TileEditModalFormShape>();

  const onSubmit = handleSubmit((formData) => {
    // If onSaveData is provided, delegate to the caller (deferred persistence mode)
    if (onSaveData) {
      onSaveData(formData);
      return Promise.resolve();
    }

    // Otherwise, update React Flow directly (immediate mode)
    // This path is used by non-Estate Waterfall usages of TileEditModal
    // Determine if the component type changed (tile <> icon)
    const componentTypeChanged =
      node.data.componentType !== formData.componentType;
    const notesChangedLength =
      formData.notes !== undefined &&
      formData.notes.length !==
        (typeof node.data.notes === 'string' ? node.data.notes.length : 0);

    // If we toggled between tile and icon variants, or if the notes changed
    // length, clear the height so it adjusts to the new content.
    if (componentTypeChanged || notesChangedLength) {
      const n = getNode(node.id);
      updateNode(node.id, {
        width: n?.width,
        height: undefined,
        measured: {
          width: n?.measured?.width,
          height: undefined,
        },
      });
    }

    // Update the rest of the node-specific data
    const update: Partial<TileNode['data']> = {
      notes: formData.notes,
      componentType: formData.componentType,
      iconShape: formData.iconShape,
      estateWaterfallConfig: formData.estateWaterfallConfig,
      ...(componentTypeChanged || notesChangedLength
        ? { dimensionsFinalized: undefined }
        : {}),
    };
    updateNodeData(node.id, update);
    return Promise.resolve();
  });

  const onSuccess = () => {
    onSuccessExternal?.();
    onClose();
    setTimeout(() => {
      reset();
    }, theme.transitions.duration.leavingScreen);
  };

  useSubmitSuccessHandler(onSuccess);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      heading="Tile settings"
      rightHeaderContent={null}
      data-testid="tile-edit-modal"
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={onClose} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Save changes
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <Stack spacing={1}>
          <FormLayoutRow>
            <FormLayoutItem>
              <GraphVizNodeConfigFormField.ComponentType<TileEditModalFormShape>
                control={control}
                fieldName={
                  'componentType' as const satisfies keyof TileEditModalFormShape
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
          {showEstateWaterfallTaxBreakoutInEditModal && (
            <FormLayoutRow>
              <FormLayoutItem>
                <FormAwareSwitch<TileEditModalFormShape>
                  label="Show itemized tax values"
                  helpText="If toggled on, federal, state, and GST tax values will be shown as separate line items"
                  fieldName={'estateWaterfallConfig.showTaxBreakout'}
                  labelPosition="right"
                  control={control}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
          <FormLayoutRow>
            <FormLayoutItem>
              <TileNotesFieldWithGenerate<TileEditModalFormShape>
                mode="single"
                tileId={node.id}
                control={control}
                fieldName={
                  'notes' as const satisfies keyof TileEditModalFormShape
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </Stack>
      </form>
    </FormModal>
  );
}

export type TileEditModalProps = Omit<
  TileEditModalInnerProps,
  'node' | 'showEstateWaterfallTaxBreakoutInEditModal'
>;

/**
 * Edits the "Display as" setting and the notes of the tile that renders it.
 *
 * NOTE: This modal is not the common tile modal of all diagram editors. The
 * editors edit tiles in different modals, with different fields:
 * - Estate waterfall (`EstateWaterfallTileEditModal`) and entity diagram
 *   (`EntityDiagramTileEditModal`) use this modal. It does not edit the tile
 *   text, because those tiles get their text from household data.
 * - This modal has estate waterfall settings: the
 *   `showEstateWaterfallTaxBreakoutInEditModal` tile data flag and the
 *   `estateWaterfallConfig` field.
 * - The presentation diagram does not use this modal.
 *   `PresentationDiagramTileEditModal` is a copy of this form. It also edits
 *   the name (`lineOne`) and the type (`variant`), uses the notes field
 *   without "Generate", and keeps a missing icon shape. This modal sets a
 *   missing icon shape to circle on save.
 * - The artifact diagram builder uses `TileTextEditModal`. It edits the three
 *   text lines only.
 * - The layouts are different: this modal uses `FormLayoutRow` rows, and the
 *   presentation and builder modals use a `Stack` with more spacing.
 * - Without `onSaveData`, this modal writes to React Flow directly. Only the
 *   default `TileNode` modal uses that path.
 * Moving all editors to one modal needs design input: https://linear.app/luminary/issue/T1-7153
 */
export function TileEditModal(props: TileEditModalProps) {
  const node = useNode<TileNode>();

  const formMethods = useForm<TileEditModalFormShape>({
    defaultValues: {
      notes: typeof node.data.notes === 'string' ? node.data.notes : undefined,
      componentType: node.data.componentType ?? GraphVizNodeComponentType.Tile,
      iconShape: node.data.iconShape ?? GraphVizIconShape.Circle,
      estateWaterfallConfig: node.data.estateWaterfallConfig,
    },
  });

  return (
    // Internal editor UI: force the Luminary theme. Some surfaces mount this
    // under a CustomerThemeProvider ("Preview in brand style"), which would
    // otherwise tint the primary buttons with the tenant's brand colors.
    <LuminaryThemeProvider>
      <FormProvider {...formMethods}>
        <FormConfigurationProvider
          value={{ optionalDisplayType: 'optional-label' }}
        >
          <TileEditModalInner
            node={node}
            {...props}
            showEstateWaterfallTaxBreakoutInEditModal={
              node.data.showEstateWaterfallTaxBreakoutInEditModal ?? false
            }
          />
        </FormConfigurationProvider>
      </FormProvider>
    </LuminaryThemeProvider>
  );
}
